import { useEffect, useMemo, useState } from 'react'
import cx from 'classnames'
import styles from './Confetti.module.css'

interface ConfettiProps {
  /** Number of confetti pieces */
  count?: number
  /** Duration of the animation in seconds */
  duration?: number
  /** Array of colors to use for confetti */
  colors?: string[]
  /** Whether the animation is active */
  active?: boolean
  /** Callback when animation completes */
  onComplete?: () => void
}

interface ConfettiPiece {
  id: number
  left: number
  delay: number
  duration: number
  color: string
  rotation: number
  size: number
}

const DEFAULT_COLORS = [
  '#f472b6', // pink-400
  '#a78bfa', // violet-400
  '#60a5fa', // blue-400
  '#34d399', // emerald-400
  '#fbbf24', // amber-400
  '#f87171', // red-400
]

export function Confetti({
  count = 50,
  duration = 3,
  colors = DEFAULT_COLORS,
  active = true,
  onComplete,
}: ConfettiProps) {
  const [isVisible, setIsVisible] = useState(active)

  const pieces = useMemo<ConfettiPiece[]>(() => {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 0.5,
      duration: duration * 0.8 + Math.random() * duration * 0.4,
      color: colors[Math.floor(Math.random() * colors.length)],
      rotation: Math.random() * 360,
      size: 8 + Math.random() * 8,
    }))
  }, [count, duration, colors])

  useEffect(() => {
    setIsVisible(active)
    if (active && onComplete) {
      const timer = setTimeout(() => {
        onComplete()
      }, (duration + 0.5) * 1000)
      return () => clearTimeout(timer)
    }
  }, [active, duration, onComplete])

  if (!isVisible) return null

  return (
    <div className={styles.Confetti} aria-hidden="true">
      {pieces.map((piece) => (
        <div
          key={piece.id}
          className={cx(styles.piece, {
            [styles.pieceRect]: piece.id % 3 === 0,
            [styles.pieceCircle]: piece.id % 3 === 1,
            [styles.pieceSquare]: piece.id % 3 === 2,
          })}
          style={
            {
              '--confetti-left': `${piece.left}%`,
              '--confetti-delay': `${piece.delay}s`,
              '--confetti-duration': `${piece.duration}s`,
              '--confetti-color': piece.color,
              '--confetti-rotation': `${piece.rotation}deg`,
              '--confetti-size': `${piece.size}px`,
            } as React.CSSProperties
          }
        />
      ))}
    </div>
  )
}
